import clsx from 'clsx'
import { InfoIcon } from 'lucide-react'
import React from 'react'

import { Tooltip } from 'slab'

interface PlanCardPremiumProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  align?: 'left' | 'right';
  amount: number;
  remainder?: number;
  displayMode?: 'compact' | 'large';
  isTieredAllowance?: boolean;
  audience?: 'member' | 'employer';
}

function formatRemainder (amount: number) {
  const formatterOptions: Intl.NumberFormatOptions =
    amount % 1
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 }

  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    ...formatterOptions,
  })
}

export const TIERED_ALLOWANCE_TOOLTIP_TEXT =
  "Your allowance covers your insurance costs, up to a set limit. You'll pay the difference if you add dependents or pick a more expensive plan."

export function PlanCardPremium ({
  align = 'right',
  amount,
  remainder,
  displayMode = 'large',
  isTieredAllowance,
  audience = 'member',
  ...props
}: PlanCardPremiumProps) {
  const isMember = audience === 'member'
  const formattedAmount = amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  function renderRemainder () {
    if (remainder === undefined || null) return null

    const classNames = clsx({
      'text-right': align === 'right',
      'text-gray-800': remainder === 0,
      'text-green-400': remainder > 0,
      'text-red-500': remainder < 0,
      'body-sm': displayMode === 'compact',
    })

    if (isTieredAllowance) {
      return (
        <div
          className={clsx('body-sm', {
            'text-right': align === 'right',
            'text-left': align === 'left',
          })}
        >
          <span className='flex items-center text-gray-500 justify-end'>
            {isMember ? 'You pay' : 'Employee pays'}
            <Tooltip content={TIERED_ALLOWANCE_TOOLTIP_TEXT}>
              <InfoIcon
                className='ml-1 w-4 h-4 hover:cursor-pointer'
                aria-label='Info icon'
              />
            </Tooltip>
          </span>

          <span className='body-sm-bold'>
            {formatRemainder(Math.abs(remainder))}
          </span>
          <span aria-hidden>/mo </span>
          <span className='sr-only'> per month</span>
        </div>
      )
    } else {
      return remainder >= 0
        ? (
          <div className={classNames}>
            {formatRemainder(remainder)}
            <span aria-hidden>/mo </span>
            <span className='sr-only'> per month</span>
            remaining
          </div>
          )
        : (
          <div className={classNames}>
            {isMember ? 'You pay' : 'Employee pays'} {formatRemainder(Math.abs(remainder))}
            <span aria-hidden>/mo </span>
            <span className='sr-only'> per month</span>
          </div>
          )
    }
  }

  return (
    <div
      {...props}
      className={clsx('flex flex-col', {
        'items-end': align === 'right',
        'items-start': align === 'left',
        'gap-0.5': displayMode === 'compact',
        'gap-1': displayMode === 'large',
      })}
    >
      <div
        className={clsx('font-semibold text-gray-800', {
          'body-sm': displayMode === 'compact',
          'heading-md': displayMode === 'large',
        })}
      >
        <span className='sr-only'>Premium amount: </span>
        {formattedAmount}
      </div>
      {renderRemainder()}
    </div>
  )
}

PlanCardPremium.displayName = 'PlanCard.Premium'
